import axios from 'axios'
import auth from '@/utils/auth'
import FileSaver from 'file-saver'

const baseURL = window.apiBaseUrl || `${window.location.protocol}//${window.location.host}/api`
const csrfToken = document.head.querySelector('meta[name="csrf-token"]').content

const requester = axios.create({
  baseURL
})

const debug = process.env.NODE_ENV !== 'production'

let isTokenRef = false

export default class BaseApi {
  async checkToken () {
    try {
      if (Number(auth.tokenExp) - Date.now() <= 30000) {
        await this.refreshToken()
      }
    } catch (e) {
    }
    isTokenRef = false
  }

  async request (method, url, data, additionalHeaders) {
    const headers = {
      'X-CSRF-TOKEN': csrfToken
    }
    const doRequest = async () => {
      // await refreshToken()

      await this.checkToken()

      if (auth.token) {
        headers.Authorization = auth.token
      }

      if (additionalHeaders) {
        Object.assign(headers, additionalHeaders)
      }

      const requestConfig = { method, url, headers }

      if (data) {
        requestConfig.data = data
      }

      const response = await requester(requestConfig)

      logResult(response)
      return unwrapData(response)
    }
    let isUnauthenticatedWithOldRequestToken = false
    let retries = 3
    do {
      try {
        return await doRequest()
      } catch (e) {
        isUnauthenticatedWithOldRequestToken = e.request.status === 401 && headers.Authorization !== auth.token
        if (isUnauthenticatedWithOldRequestToken) {
          retries--
          return
        }
        return handleError(e)
      }
    } while (isUnauthenticatedWithOldRequestToken && retries)
    return doRequest()
  }

  post (url, data, additionalHeaders) {
    return this.request('post', url, data, additionalHeaders)
  }

  put (url, data, additionalHeaders) {
    return this.request('put', url, data, additionalHeaders)
  }

  get (url, data, additionalHeaders) {
    return this.request('get', url, data, additionalHeaders)
  }

  delete (url, data, additionalHeaders) {
    return this.request('delete', url, data, additionalHeaders)
  }

  async downloadAsFile (requestConfig, fileMime, fileName) {
    const config = Object.assign({
      responseType: 'arraybuffer',
      headers: {
        Authorization: auth.token,
        Accept: fileMime
      }
    }, requestConfig)
    try {
      const response = await BaseApi.requester(config)
      const blob = new Blob([response.data], {
        type: fileMime
      })
      FileSaver.saveAs(blob, fileName)
    } catch (e) {
      return Promise.reject(JSON.parse(String.fromCharCode.apply(null, new Uint8Array(e.response.data))))
    }
  }

  async refreshToken () {
    if (!isTokenRef) {
    // we have to use here a separate request pipeline
    // otherwise we would run into a recursive loop with this.request
      try {
        isTokenRef = true
        const response = await requester({
          url: 'user/token-refresh',
          method: 'post',
          headers: {
            Authorization: auth.token
          },
          data: {
            token: auth.token.replace('Bearer ', '')
          }
        })
        logResult(response)
        this.setAuth(unwrapData(response))
      } catch (e) {
        this.setAuth(false)
      }
      isTokenRef = false
    }
  }

  setAuth (authObj) {
    if (!authObj) {
      auth.logout()
      return
    }
    auth.user = authObj.user
    auth.token = authObj.token
    auth.tokenExp = tokenExpires(authObj.tokenLifetimeMinutes)
  }

  static get requester () {
    return requester
  }

  static get baseUrl () {
    return window.apiBaseUrl
  }
}

function handleError (errorResponse) {
  console.dir(errorResponse)
  let response = errorResponse.response

  console.error(`[Failed ${response.config.method.toUpperCase()}] ${response.config.url}:`, response.data)
  return Promise.reject(response.data)
}

function logResult (response) {
  if (debug) {
    console.info(`[${response.config.method}] ${response.config.url}:`, response.data)
  }
}

function unwrapData (response) {
  return response.data
}

function tokenExpires (tokenLifetimeMinutes) {
  const fiveMinutesOffsetInMilliseconds = 3e5
  const tokenLifetimeMilliseconds = tokenLifetimeMinutes * 6e4
  const now = new Date().getTime()
  return now + tokenLifetimeMilliseconds - fiveMinutesOffsetInMilliseconds
}
